import { useCallback, useContext } from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import {
  saveKeyword,
  loadKeywords,
  getKeyFromKeyword,
} from '@bingads-webui-campaign-react/search-campaign-data';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { ScopeContext } from '@bingads-webui-react/scope-service';

import { parseKeywords, parseKeyword, maxKeywords } from './util/parser';

const savedKeywords = {};
const updateSavedKeywords = (entity, keywordSuggestionsAdded, activity) => {
  if (entity && entity.Keywords) {
    const successfulKeywords = _.filter(entity.Keywords, keyword => keyword.Id);
    const newlyCreatedKeywords = [];
    _.each(successfulKeywords, (keyword) => {
      const key = getKeyFromKeyword(keyword);
      if (keyword.Status === 'Deleted') {
        delete savedKeywords[key];
      } else {
        savedKeywords[key] = keyword;
        newlyCreatedKeywords.push(keyword.Text);
      }
    });
    const numberOfCreatedKeywords = _.size(newlyCreatedKeywords);
    if (numberOfCreatedKeywords > 0) {
      const keywordsImportedFromPlanner = _.filter(keywordSuggestionsAdded, suggestedKeyword => _.indexOf(newlyCreatedKeywords, suggestedKeyword.Keyword) !== -1);
      // eslint-disable-next-line no-unused-expressions
      activity && activity.signal(`${JSON.stringify({
        keywordsCreated: numberOfCreatedKeywords,
        campaignId: entity && entity.Campaign && entity.Campaign.Id,
        adGroupId: entity.Id,
        suggestionsUsed: _.size(keywordsImportedFromPlanner),
        detailedSuggestionsUsed: _.chain(keywordsImportedFromPlanner)
          .groupBy(keyword => keyword.RequestId)
          .mapObject(reqGroup => _.pluck(reqGroup, 'Keyword'))
          .value(),
      })}`, 'createKeyword/updateViewModels');
      return true;
    }
    return false;
  }
  return false;
};

export const useKeywordsRepository = () => {
  const i18n = useContext(I18nContext);
  const scenario = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid, campaignId: campaignIdFromScope, adGroupId: adGroupIdFromScope } = scope.values;
  const { send, state } = useWizardService();
  const {
    campaignId: campaignIdFromWizardData,
    adGroupId: adGroupIdFromWizardData,
    keywordsInput: savedKeywordsInput,
    previousLanguages: languages,
    previousLocation: locations,
  } = state.context.wizardData;

  const campaignId = campaignIdFromWizardData || campaignIdFromScope;
  const adGroupId = adGroupIdFromWizardData || adGroupIdFromScope;

  const load = useCallback(() => {
    const loadPromises = {
      keywords: adGroupId ? loadKeywords(cid, aid, campaignId, adGroupId, maxKeywords) : Promise.resolve({}),
    };

    return Promise.props(loadPromises)
      .then(({
        keywords,
      }) => {
        const { validResult } = parseKeywords({
          keywordsTextarealVal: savedKeywordsInput,
          i18n,
        });
        if (keywords && keywords.value && _.isArray(keywords.value)) {
          _.each(keywords.value, (keyword) => {
            savedKeywords[getKeyFromKeyword(keyword)] = keyword;
          });
        }
        return Promise.resolve({
          Languages: languages || [],
          locationCriterions: locations || [],
          keywordsInput: savedKeywordsInput,
          validationResult: _.isUndefined(savedKeywordsInput) ? { valid: true, error: [] } : validResult,
          keywordSuggestionsAdded: [],
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(`Load keywords failed: ${error}`);
        activity.signal(`onKeywordsLoadFailed - ${JSON.stringify({
          campaignId,
          adGroupId,
          error,
        })}`);
        return Promise.reject(error);
      });
  }, [adGroupId, cid, aid, campaignId, savedKeywordsInput, i18n, languages, locations, activity]);

  const save = useCallback(({ data }) => {
    const { keywordsInput, keywordSuggestionsAdded } = data;
    // saved keywords
    const savedKeywordKeys = _.keys(savedKeywords);

    // current keywords
    const { uniqKeywords: currentKeywordTexts, validResult } = parseKeywords({
      keywordsTextarealVal: keywordsInput,
      i18n,
    });

    // check validation
    if (!validResult.valid) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        data: {
          ...data,
          keywordsInput,
          validationResult: validResult,
        },
      });
    }

    const keywordTextsNeedAdd = _.difference(currentKeywordTexts, savedKeywordKeys);
    const keywordTextsNeedDelete = _.difference(savedKeywordKeys, currentKeywordTexts);
    let keywordObjsNeedAdd = [];
    let keywordObjsNeedDelete = [];
    if (!_.isEmpty(keywordTextsNeedAdd)) {
      keywordObjsNeedAdd = _.map(keywordTextsNeedAdd, (keyword) => {
        const keywordObj = parseKeyword(keyword, i18n);
        return {
          ...keywordObj,
          AdGroup: { Id: adGroupId },
        };
      });
    }

    if (!_.isEmpty(keywordTextsNeedDelete)) {
      keywordObjsNeedDelete = _.map(keywordTextsNeedDelete, (keyword) => {
        const keywordObj = parseKeyword(keyword, i18n);
        return {
          ...keywordObj,
          AdGroup: { Id: adGroupId },
          Status: 'Deleted',
          Id: savedKeywords[keyword].Id,
        };
      });
    }

    const keywords = keywordObjsNeedAdd.concat(keywordObjsNeedDelete);

    if (!_.isEmpty(keywords)) {
      // eslint-disable-next-line no-unused-expressions
      activity && activity.signal(`onSaveKeywords - ${JSON.stringify({
        KeywordsInput: keywords,
      })}`);

      const saveKeywordPromise = () => saveKeyword({
        campaignId,
        adGroupId,
        keywords,
      }, {
        scenario: scenario.name,
        i18n,
        activityLogger: activity,
      });

      return saveKeywordPromise()
        .then((entity) => {
          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
            keywordsInput,
          });
          const clearKeywordSuggestionsAdded = updateSavedKeywords(entity, keywordSuggestionsAdded, activity);
          return Promise.resolve({
            ...data,
            keywordsInput,
            validResult,
            keywordSuggestionsAdded: clearKeywordSuggestionsAdded ? [] : keywordSuggestionsAdded,
          });
        })
        .catch((error) => {
          const {
            serverErrors = [],
            systemErrors = [],
            entity,
          } = error || {};

          const clearKeywordSuggestionsAdded = updateSavedKeywords(entity, keywordSuggestionsAdded, activity);

          if (_.isEmpty(serverErrors) && _.isEmpty(systemErrors)) {
            send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
              keywordsInput,
            });
            return Promise.resolve({
              ...data,
              keywordsInput,
              validResult,
              keywordSuggestionsAdded: clearKeywordSuggestionsAdded ? [] : keywordSuggestionsAdded,
            });
          }

          // eslint-disable-next-line no-console
          console.error(`Save keywords failed: ${error}`);
          activity.signal(`onKeywordsSaveFailed - ${JSON.stringify({
            data,
            error,
          })}`);

          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject({
            data: {
              ...data,
              keywordsInput,
              validResult,
              keywordSuggestionsAdded: clearKeywordSuggestionsAdded ? [] : keywordSuggestionsAdded,
            },
            error: {
              serverErrors: _.flatten(serverErrors.filter(val => val !== null)),
              systemErrors: _.flatten(systemErrors.filter(val => val !== null)),
            },
          });
        });
    }

    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      keywordsInput,
    });

    return Promise.resolve({
      ...data,
      keywordsInput,
      validResult,
    });
  }, [i18n, send, adGroupId, activity, campaignId, scenario.name]);

  return {
    load,
    save,
  };
};
